import {
  FormDescription,
  FormField,
  FormLabel,
  FormMessage,
} from "@hortis/ui/form";
import { Input } from "@hortis/ui/input";
import { Tab, TabContent, TabHeader, TabList } from "@hortis/ui/tab/tab";
import { AccessionNumberFormat } from "generated/graphql";
import { useEffect } from "react";
import { Controller, type FieldError, useFormContext } from "react-hook-form";
import { type AccessionDataZod } from "src/features/create-accession/components/accession-data-form/schema";
import { twMerge } from "tailwind-merge";

// Displays all field errors (from both schema validation and manual setting) without duplication
const ErrorMessages = ({
  error,
  dataCy,
}: {
  error?: FieldError;
  dataCy: string;
}) => {
  if (error == null) {
    return;
  }
  if (!error.types && error.message == null) {
    return null;
  }

  return (
    <>
      {error.message != null && (
        <FormMessage className="text-wrap! text-error-400" data-cy={dataCy}>
          {error.message}
        </FormMessage>
      )}
      {error.types &&
        Object.values(error.types)
          .filter((message) => message !== error.message)
          .map((message, index) => (
            <FormMessage
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="text-wrap! text-error-400"
              data-cy={dataCy}
            >
              {message}
            </FormMessage>
          ))}
    </>
  );
};

const AccessionNumberInput = ({
  tab,
  showAutomaticTab = false,
  showLabel = false,
}: {
  tab: AccessionNumberFormat | "AUTOMATIC";
  showAutomaticTab?: boolean;
  showLabel?: boolean;
}) => {
  const { control, formState, setValue, watch, clearErrors } =
    useFormContext<Partial<AccessionDataZod>>();

  const accessionDate = watch("accessionDate");

  // If the default accessionNumber format is Prefix suffix we know that we're updating an accession rather than creating one. We only want to show the automatic tab if we're updating an exogenously numbered accession or we're creating a compeletely new accession
  const hideAutomaticTab =
    !showAutomaticTab &&
    formState.defaultValues?.accessionNumberFormat ===
      AccessionNumberFormat.PrefixSuffix &&
    accessionDate?.getFullYear() ===
      formState.defaultValues?.accessionDate?.getFullYear();

  useEffect(() => {
    if (
      accessionDate?.getFullYear() !==
      formState.defaultValues?.accessionDate?.getFullYear()
    ) {
      setValue("accessionNumberFormat", null);
      setValue("generateAccessionNumber", true);
      setValue("accessionNumberSuffix", null);
    }
  }, [accessionDate]);

  return (
    <div className="flex max-w-full flex-col gap-1.5">
      {showLabel && <FormLabel required>Accession number</FormLabel>}
      <FormField
        name="accessionNumberFormat"
        control={control}
        // eslint-disable-next-line sonarjs/cognitive-complexity
        render={() => (
          <Tab
            value={tab}
            className="w-full border-none shadow-none!"
            onValueChange={(format) => {
              clearErrors([
                "accessionNumberSuffix",
                "exogenousAccessionNumber",
              ]);
              if (format === AccessionNumberFormat.Exogenous) {
                setValue(
                  "accessionNumberFormat",
                  AccessionNumberFormat.Exogenous,
                );
                setValue("generateAccessionNumber", false);
              } else if (format === AccessionNumberFormat.PrefixSuffix) {
                setValue(
                  "accessionNumberFormat",
                  AccessionNumberFormat.PrefixSuffix,
                );
                setValue("generateAccessionNumber", false);
              } else if (!hideAutomaticTab) {
                setValue("accessionNumberFormat", null);
                setValue("generateAccessionNumber", true);
              }
            }}
            size="sm"
            fullWidth={false}
          >
            <TabList className="w-fit rounded-tr-xl!">
              {!hideAutomaticTab && (
                <TabHeader
                  className={twMerge(
                    "rounded-tl-lg! border-l! border-t ring-inset ring-grey-200/70 focus-visible:ring-3",
                    tab !== "AUTOMATIC" && "rounded-b-none!",
                    tab === "AUTOMATIC" && "border-b",
                  )}
                  value={"AUTOMATIC"}
                >
                  Automatic
                </TabHeader>
              )}
              <TabHeader
                className={twMerge(
                  "rounded-b-none! border-l! border-t ring-inset ring-grey-200/70 focus-visible:ring-3",
                  tab === "AUTOMATIC" && "border-b",
                )}
                value={AccessionNumberFormat.PrefixSuffix}
              >
                Standard
              </TabHeader>
              <TabHeader
                className={twMerge(
                  "rounded-tr-lg! border-r border-t ring-inset ring-grey-200/70 focus-visible:ring-3",
                  tab === "AUTOMATIC" && "border-b",
                  tab !== "AUTOMATIC" && "rounded-b-none!",
                )}
                value={AccessionNumberFormat.Exogenous}
              >
                Custom
              </TabHeader>
            </TabList>
            <TabContent
              className="outline-hidden"
              value={AccessionNumberFormat.PrefixSuffix}
              tabIndex={-1}
            >
              <div className="flex rounded-b-lg rounded-r-lg border border-grey-300 ring-inset ring-grey-200/70 focus-within:ring-[3px]">
                <div className="flex flex-1 items-center justify-center pl-3">
                  <div
                    data-cy="accession-number-prefix"
                    className="flex h-7 w-full items-center justify-center rounded-md border border-grey-300 px-3 py-2 text-sm font-medium text-grey-700"
                  >
                    {accessionDate?.getFullYear()}
                  </div>
                </div>
                <Controller
                  name="accessionNumberSuffix"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <div className="relative flex flex-1">
                        <div className="pointer-events-none absolute inset-0 flex items-center ">
                          <span
                            className="w-full pr-3 text-right text-grey-400"
                            aria-hidden="true"
                          >
                            {field.value?.toString().padStart(4, "0") ?? "0000"}
                          </span>
                        </div>
                        <Input
                          {...field}
                          type="text"
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(
                                    e.target.value
                                      .replaceAll(/\D/g, "")
                                      .slice(0, 4),
                                  );
                            field.onChange(value);
                          }}
                          value={field.value ?? ""}
                          error={
                            error != null &&
                            (error.message != null ||
                              (error.types &&
                                Object.keys(error.types).length > 0))
                          }
                          data-cy="accession-number-suffix"
                          className={twMerge(
                            "w-full rounded-none rounded-r-lg border-none shadow-none ring-0! transition-all [&_>_input]:z-10 [&_>_input]:w-full [&_>_input]:min-w-0 [&_>_input]:pr-3 [&_>_input]:text-right",
                            "[&_>_input:focus]:shadow-[inset_0_3px_0_0_rgb(229,231,235,0.7),inset_0_-3px_0_0_rgb(229,231,235,0.7),inset_-3px_0_0_0_rgb(229,231,235,0.7),0_0_0_3px_rgb(229,231,235,0.7)]",
                            "[&_>_input:focus]:rounded-r-lg [&_>_input:focus]:shadow-[inset_0_3px_0_0_rgb(229,231,235,0.7),inset_0_-3px_0_0_rgb(229,231,235,0.7),inset_-3px_0_0_0_rgb(229,231,235,0.7),0_0_0_3px_rgb(229,231,235,0.7)]",
                          )}
                          inputMode="numeric"
                        />
                      </div>
                    );
                  }}
                />
              </div>
            </TabContent>
            <TabContent
              className="outline-hidden"
              value={AccessionNumberFormat.Exogenous}
              tabIndex={-1}
            >
              <Controller
                name="exogenousAccessionNumber"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    placeholder="Enter custom number..."
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                    }}
                    value={field.value ?? ""}
                    error={
                      error != null &&
                      (error.message != null ||
                        (error.types && Object.keys(error.types).length > 0))
                    }
                    data-cy="exogenous-accession-number"
                    className="rounded-none! rounded-r-lg! rounded-bl-lg! border ring-inset! transition-all"
                  />
                )}
              />
            </TabContent>
          </Tab>
        )}
      />
      {tab === "AUTOMATIC" && (
        <FormDescription className="text-wrap! text-grey-500">
          Accession will be assigned the next available number
        </FormDescription>
      )}
      <ErrorMessages
        error={formState.errors.accessionNumberSuffix}
        dataCy="accession-number-suffix-error"
      />
      <ErrorMessages
        error={formState.errors.exogenousAccessionNumber}
        dataCy="exogenous-accession-number-error"
      />
    </div>
  );
};

export { AccessionNumberInput };
